import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { SITE_NAV, FOOTER_MENU } from '../../lib/site';
import { Logo } from './Logo';
import { SiteChat } from '../SiteChat';

/**
 * Public website chrome. `overlay` makes the header sit transparently over a hero
 * photograph until the visitor scrolls, exactly as lotusliving.co.uk does.
 */
export function SiteLayout({ children, overlay = false, chat = true, footer = true }: { children: ReactNode; overlay?: boolean; chat?: boolean; footer?: boolean }) {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  // The menu belongs to the page it was opened on, so any navigation closes it without an effect.
  const [menuOn, setMenuOn] = useState<string | null>(null);
  const menu = menuOn === pathname;
  const closeMenu = () => setMenuOn(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }); }, []);

  // While the phone menu is open: no page scroll behind it, Escape closes it, and it shuts if the window grows to desktop.
  useEffect(() => {
    if (!menu) return;
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOn(null); };
    const mq = window.matchMedia('(min-width: 1280px)');
    const onMq = () => { if (mq.matches) setMenuOn(null); };
    window.addEventListener('keydown', onKey);
    mq.addEventListener('change', onMq);
    return () => { html.style.overflow = prev; window.removeEventListener('keydown', onKey); mq.removeEventListener('change', onMq); };
  }, [menu]);

  const dark = overlay && !scrolled && !menu;

  return (
    <div className="site min-h-screen overflow-x-clip bg-white font-sans text-ink-800">
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,padding,box-shadow,border-color] duration-500 ease-[cubic-bezier(.16,1,.3,1)] ${
          dark
            ? 'border-b border-white/10 bg-transparent py-4 sm:py-5'
            : `border-b border-ink-200/70 bg-white/90 backdrop-blur-xl ${scrolled ? 'py-2.5 shadow-[0_8px_30px_-18px_rgba(33,32,32,0.25)]' : 'py-3 sm:py-3.5'}`
        }`}
      >
        <div className="site-container flex items-center justify-between gap-6">
          <Logo tone="bronze" className={`w-auto transition-all duration-500 ${scrolled ? 'h-8 sm:h-9' : 'h-9 sm:h-10'}`} />

          <nav className="hidden items-center gap-7 xl:flex 2xl:gap-9">
            {SITE_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `group relative whitespace-nowrap py-2 text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    dark ? (isActive ? 'text-white' : 'text-white/75 hover:text-white') : isActive ? 'text-ink-900' : 'text-ink-500 hover:text-ink-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span className={`absolute inset-x-0 -bottom-0.5 h-px origin-left bg-current transition-transform duration-500 ease-[cubic-bezier(.16,1,.3,1)] ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-5 xl:flex">
            <Link to="/login" className={`whitespace-nowrap text-[13px] font-medium transition-colors ${dark ? 'text-white/80 hover:text-white' : 'text-ink-500 hover:text-ink-900'}`}>
              Sign in
            </Link>
          </div>

          <button
            onClick={() => setMenuOn(menu ? null : pathname)}
            className={`-mr-2 grid h-11 w-11 place-items-center rounded-full xl:hidden ${dark ? 'text-white' : 'text-ink-900'}`}
            aria-label={menu ? 'Close menu' : 'Open menu'}
            aria-expanded={menu}
            aria-controls="site-menu"
          >
            <span className="relative block h-3.5 w-6" aria-hidden="true">
              <span className={`absolute left-0 top-0 h-[1.5px] w-full bg-current transition-transform duration-500 ease-[cubic-bezier(.16,1,.3,1)] ${menu ? 'translate-y-[6.5px] rotate-45' : ''}`} />
              <span className={`absolute left-0 top-[6.5px] h-[1.5px] w-full bg-current transition-opacity duration-300 ${menu ? 'opacity-0' : ''}`} />
              <span className={`absolute left-0 top-[13px] h-[1.5px] w-full bg-current transition-transform duration-500 ease-[cubic-bezier(.16,1,.3,1)] ${menu ? '-translate-y-[6.5px] -rotate-45' : ''}`} />
            </span>
          </button>
        </div>
      </header>

      {menu && (
        <div id="site-menu" className="site-menu-panel fixed inset-0 z-[45] flex flex-col overflow-y-auto bg-white pt-[68px] sm:pt-[76px] xl:hidden">
          <nav className="site-container flex flex-1 flex-col py-6">
            {SITE_NAV.map((item, i) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={closeMenu}
                style={{ '--i': i } as React.CSSProperties}
                className={({ isActive }) => `site-menu-item flex items-center justify-between border-b border-ink-100 py-4 font-display text-[30px] leading-none tracking-[-0.01em] sm:text-[36px] ${isActive ? 'text-bronze-500' : 'text-ink-900'}`}
              >
                {item.label}
                <ArrowUpRight className="h-4 w-4 text-ink-300" />
              </NavLink>
            ))}
            <div className="site-menu-item mt-auto flex flex-col gap-4 pt-10 pb-[max(1.5rem,env(safe-area-inset-bottom))]" style={{ '--i': SITE_NAV.length } as React.CSSProperties}>
              <Link to="/login" onClick={closeMenu} className="py-2 text-center text-sm font-medium text-ink-500">Resident or staff? Sign in</Link>
            </div>
          </nav>
        </div>
      )}

      <main className={overlay ? '' : 'pt-[68px] sm:pt-[76px]'}>{children}</main>

      {footer && <SiteFooter />}
      {chat && <SiteChat />}
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="relative z-10 bg-ink-900 pb-28 pt-14 text-white sm:pb-12 sm:pt-16 lg:pt-20">
      <div className="site-container">
        <div className="flex flex-col justify-between gap-12 lg:flex-row lg:items-end">
          <nav className="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 sm:gap-16">
            {Object.entries(FOOTER_MENU).map(([group, items]) => (
              <div key={group}>
                <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-bronze-400">{group}</div>
                <ul className="space-y-1">
                  {items.map((it) => (
                    <li key={it.label}>
                      {'external' in it && it.external
                        ? <a href={it.to} target="_blank" rel="noreferrer" className="inline-block py-1 text-sm text-white/70 transition hover:text-white">{it.label}</a>
                        : <Link to={it.to} className="inline-block py-1 text-sm text-white/70 transition hover:text-white">{it.label}</Link>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <div className="flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between lg:flex-col lg:items-end">
            <div className="flex flex-wrap items-center gap-4 opacity-90 sm:gap-6">
              {[['ombudsman', 'The Property Ombudsman'], ['nrla', 'National Residential Landlords Association'], ['tds', 'Tenancy Deposit Scheme']].map(([k, alt]) => (
                <img key={k} src={`/images/badges/${k}.png`} alt={alt} className="h-10 w-auto rounded bg-white/95 p-1.5 sm:h-12" loading="lazy" />
              ))}
            </div>
            <Logo tone="white" className="h-9 sm:h-10" />
          </div>
        </div>
        <div className="mt-12 flex flex-col gap-2 border-t border-white/10 pt-6 text-[11px] text-white/40 sm:mt-14 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3 lg:pr-44">
          <span>© {new Date().getFullYear()} Lotus Living. Greater London · Dubai South.</span>
          <span>Platform demo · every price above is live</span>
        </div>
      </div>
    </footer>
  );
}
